import { Trash2 } from 'lucide-react';
import type { FormEvent, ReactNode } from 'react';
import { useIsDesktop } from '@/shared/hooks/use-is-desktop';
import { useTranslation } from '@/shared/i18n/use-translation';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button/button';
import { Sheet } from '@/shared/ui/sheet/sheet';
import { SheetContent } from '@/shared/ui/sheet/sheet-content';
import { SheetDescription } from '@/shared/ui/sheet/sheet-description';
import { SheetFooter } from '@/shared/ui/sheet/sheet-footer';
import { SheetHeader } from '@/shared/ui/sheet/sheet-header';
import { SheetTitle } from '@/shared/ui/sheet/sheet-title';

type EditorSheetProps = {
  readonly title: string;
  readonly description?: ReactNode;
  readonly children: ReactNode;
  readonly saving?: boolean;
  readonly footerExtra?: ReactNode;
  readonly deleteLabel?: string;
  readonly onClose: () => void;
  readonly onSubmit: () => void | Promise<void>;
  readonly onDelete?: () => void | Promise<void>;
};

/** The editing surface for every record: a bottom sheet on phones, a side panel on larger screens. */
export function EditorSheet({
  title,
  description,
  children,
  saving,
  footerExtra,
  deleteLabel,
  onClose,
  onSubmit,
  onDelete,
}: EditorSheetProps) {
  const desktop = useIsDesktop();
  const { t } = useTranslation();
  const remove = deleteLabel ?? t('Delete');

  function submit(event: FormEvent) {
    event.preventDefault();
    void onSubmit();
  }

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side={desktop ? 'right' : 'bottom'}
        className={cn('gap-0 p-0', desktop ? 'w-full sm:max-w-xl' : 'max-h-[92dvh] rounded-t-2xl')}
      >
        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col" noValidate>
          <SheetHeader className="border-b px-5 pt-5 pb-4">
            {!desktop && <div className="mx-auto -mt-2 mb-2 h-1 w-10 rounded-full bg-muted-foreground/25" />}
            <SheetTitle className="pr-8 text-lg">{title}</SheetTitle>
            {description && <SheetDescription>{description}</SheetDescription>}
          </SheetHeader>
          <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto overscroll-contain px-5 py-5">
            {children}
          </div>
          <SheetFooter className="flex-row items-center gap-2 border-t px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {onDelete && (
              // No confirmation: it goes to the trash and the toast offers to undo it.
              <Button
                type="button"
                variant="ghost"
                size="lg"
                className="text-destructive"
                aria-label={remove}
                onClick={() => void onDelete()}
              >
                <Trash2 />
                <span className="max-sm:sr-only">{remove}</span>
              </Button>
            )}
            {footerExtra}
            <div className="ml-auto flex gap-2">
              <Button type="button" variant="outline" size="lg" onClick={onClose}>
                {t('Cancel')}
              </Button>
              <Button type="submit" size="lg" disabled={saving}>
                {t('Save')}
              </Button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
